import { useEffect, useRef } from 'react';
import { createStore } from '@/shared/lib/store';

/** Насколько раньше начала реплики стартует воспроизведение, мс */
const PLAYBACK_LEAD_MS = 1000;

/** Готовность элемента, начиная с которой известна длительность записи и работает перемотка */
const HAVE_METADATA = 1;

/** Что нужно, чтобы прозвучала найденная реплика */
export type PhrasePlaybackParams = {
  /** Ключ реплики: пока он звучит, кнопка её строки имеет вид остановки */
  key: string;
  /** Адрес записи диалога */
  audioUrl: string;
  /** Начало реплики от начала записи, мс */
  startedAt: number;
  /** Конец реплики от начала записи, мс; null — у реплики нет конца, играем до конца записи */
  finishedAt: number | null;
  /** Насколько раньше начала реплики начинать, мс; по умолчанию — секунда разгона */
  leadMs?: number;
  /** Насколько дольше конца реплики играть, мс; по умолчанию — ровно по конец реплики */
  tailMs?: number;
};

/** Что нужно, чтобы начать получение записи реплики */
export type PhraseLoadingParams = {
  /** Ключ реплики, чью запись получают */
  key: string;
  /** Чем прекратить ожидание записи, заказанной у источника */
  cancelWait: () => void;
  /**
   * Включает сопровождение звуком: его включает именно нажатие прослушивания, а не запуск
   * реплики следом за курсором — иначе сопровождение включало бы само себя.
   */
  withFollow?: boolean;
};

/** Единственный проигрыватель приложения: одновременно звучит не более одной реплики */
let player: HTMLAudioElement | null = null;

/** Ключ запущенной реплики: по первому звуку именно её строка получает вид остановки */
let startedKey: string | null = null;

/** Прекращает ожидание записи, заказанной у источника; null — ничего не ждём */
let cancelRecordingWait: (() => void) | null = null;

/** Момент записи, на котором воспроизведение обрывается; null — играем до конца записи */
let stopAtSec: number | null = null;

/** Отложенный старт, ждущий метаданных записи: снимается при остановке и при новом запуске */
let pendingStart: (() => void) | null = null;

/**
 * Номер текущего запуска. Запуск новой реплики останавливает предыдущую, и её `play()`
 * отклоняется уже после того, как звучит следующая: без сверки номера отказ прежнего
 * запуска погасил бы кнопку новой строки.
 */
let playbackToken = 0;

/** Прекращает воспроизведение вне React-дерева: остановка по воле пользователя и отказ `play()` */
export const stopPhrasePlayback = () => usePhrasePlaybackStore.getState().stop();

/** Конец звучания реплики вне React-дерева: слушатели элемента зовут её же */
const finishPhrasePlayback = () => usePhrasePlaybackStore.getState().finish();

const getPlayer = (): HTMLAudioElement => {
  if (player) return player;

  const element = new Audio();
  element.preload = 'metadata';
  element.addEventListener('timeupdate', () => {
    if (stopAtSec !== null && element.currentTime >= stopAtSec) finishPhrasePlayback();
  });
  // Вид остановки кнопка принимает по первому звуку, а не по нажатию: до него запись
  // ещё получают и перематывают, и остановка означала бы то, чего пользователь не слышит
  element.addEventListener('playing', () => {
    if (startedKey === null) return;
    usePhrasePlaybackStore.getState().markPlaying(startedKey);
  });
  element.addEventListener('ended', finishPhrasePlayback);
  element.addEventListener('error', finishPhrasePlayback);
  player = element;
  return element;
};

/** Снимает отложенный старт и останавливает запись, не трогая состояние стора */
const pausePlayer = () => {
  if (!player) return;
  if (pendingStart) {
    player.removeEventListener('loadedmetadata', pendingStart);
    pendingStart = null;
  }
  player.pause();
  stopAtSec = null;
};

const startPlayer = ({
  audioUrl,
  startedAt,
  finishedAt,
  leadMs = PLAYBACK_LEAD_MS,
  tailMs = 0,
}: Omit<PhrasePlaybackParams, 'key'>) => {
  const element = getPlayer();
  pausePlayer();

  // Реплика первой секунды записи звучит с её начала: раньше нуля отступать некуда
  const startSec = Math.max(0, (startedAt - leadMs) / 1000);
  // Конец записи запас не переступает: дальше него звучать нечему — запись кончается сама
  stopAtSec = finishedAt === null ? null : (finishedAt + tailMs) / 1000;

  if (element.src !== audioUrl) element.src = audioUrl;

  const token = ++playbackToken;
  const start = () => {
    pendingStart = null;
    element.currentTime = startSec;
    element.play().catch(() => {
      if (token === playbackToken) stopPhrasePlayback();
    });
  };

  // До метаданных браузер перемотку не принимает — ждём их и стартуем следом
  if (element.readyState >= HAVE_METADATA) start();
  else {
    pendingStart = start;
    element.addEventListener('loadedmetadata', start, { once: true });
  }
};

/** Состояние прослушивания найденной реплики */
type PhrasePlaybackStore = {
  /** Ключ звучащей реплики; null — ничего не звучит */
  playingKey: string | null;
  /** Ключ реплики, чью запись получают: от нажатия и до первого звука; null — ничего не ждём */
  loadingKey: string | null;
  /**
   * Сопровождение звуком: пока включено, реплика звучит и от одного перехода к ней,
   * без нажатия. Включает его нажатие прослушивания, выключает — остановка.
   */
  isFollowEnabled: boolean;
  /** Отмечает начало получения записи и запоминает, чем это ожидание прекратить */
  beginLoading: (params: PhraseLoadingParams) => void;
  /** Начинает реплику, прерывая ту, что звучала до неё */
  play: (params: PhrasePlaybackParams) => void;
  /** Переводит реплику из получения записи в звучание — по первому звуку */
  markPlaying: (key: string) => void;
  /** Прекращает воспроизведение по воле пользователя: сопровождение звуком выключается */
  stop: () => void;
  /** Реплика доиграла или запись получить не удалось: сопровождение звуком остаётся включённым */
  finish: () => void;
};

/** Гасит звучание и ожидание записи, не трогая сопровождение звуком */
const silence = () => {
  cancelRecordingWait?.();
  cancelRecordingWait = null;
  pausePlayer();
  startedKey = null;
};

/**
 * Прослушивание найденной реплики из списка диалогов и из перечня реплик аудита аудио.
 *
 * ## Структура
 *
 * - `playingKey` — ключ звучащей реплики; по нему кнопка строки принимает вид остановки
 * - `loadingKey` — ключ реплики, чью запись получают; по нему кнопка показывает получение
 * - `isFollowEnabled` — сопровождение звуком: слушают реплики подряд, а не по одной
 * - `beginLoading` / `play` / `markPlaying` / `stop` / `finish` — управление единственным
 *   проигрывателем приложения и ожиданием записи
 *
 * Запись играет невидимый элемент вне дерева React: она переживает перерисовку строк
 * виртуального списка, а прокрутка списка не прерывает звучание.
 *
 * Получение записи учитывается здесь же, а не в кнопке строки: ожидание единственно так же,
 * как звучание, — нажатие в другой строке прекращает и то, и другое.
 */
export const usePhrasePlaybackStore = createStore<PhrasePlaybackStore>('phrase-playback', (set) => ({
  playingKey: null,
  loadingKey: null,
  isFollowEnabled: false,
  beginLoading: ({ key, cancelWait, withFollow = false }) => {
    silence();
    cancelRecordingWait = cancelWait;
    set((state) => {
      state.playingKey = null;
      state.loadingKey = key;
      if (withFollow) state.isFollowEnabled = true;
    });
  },
  play: ({ key, ...playback }) => {
    // Запись получена — ожидать больше нечего
    cancelRecordingWait = null;
    startedKey = key;
    startPlayer(playback);
    set((state) => {
      state.playingKey = null;
      state.loadingKey = key;
    });
  },
  markPlaying: (key) => {
    set((state) => {
      state.playingKey = key;
      state.loadingKey = null;
    });
  },
  stop: () => {
    silence();
    set((state) => {
      state.playingKey = null;
      state.loadingKey = null;
      state.isFollowEnabled = false;
    });
  },
  // Реплика, кончившаяся сама, сопровождение не снимает: иначе оно гасло бы на первой же
  // реплике — доигравшей или оставшейся без записи, — и слушать подряд было бы нечем
  finish: () => {
    silence();
    set((state) => {
      state.playingKey = null;
      state.loadingKey = null;
    });
  },
}));

/**
 * Держит воспроизведение в пределах одного состава списка: смена отбора, запроса поиска
 * или открытого диалога прекращают звучание, но не сопровождение звуком — оно остаётся
 * при пользователе, пока тот не остановит прослушивание. Уход со списка снимает и его.
 */
export const usePhrasePlaybackScope = (scopeKey: string) => {
  const scopeKeyRef = useRef(scopeKey);

  useEffect(() => {
    if (scopeKeyRef.current === scopeKey) return;
    scopeKeyRef.current = scopeKey;
    finishPhrasePlayback();
  }, [scopeKey]);

  // Уход со списка — конец и прослушивания, и сопровождения звуком
  useEffect(() => stopPhrasePlayback, []);
};
